import React, { useContext, useEffect } from "react";
import { DataGrid, GridFilterPanel, Toolbar, useGridApiContext } from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { formatLabel, isComponentVisible } from "../utils/SaitUtils";
import SaitButton from "./SaitButton";
import { useSelector } from "react-redux";
import SaitButtonUpload from "./SaitButtonUpload";
import { BUTTON_UPLOAD, BUTTON_LABEL } from "../utils/ButtonTypes";
import { ModuleContext } from "../utils/Constants";
import { checkIsRoleAllowed } from "../utils/SecurityUtils";
import GridToolbarExcelButton from "../toolbar/GridToolbarExcelButton";
import GridToolbarExcelPivotButton from "../toolbar/GridToolbarExcelPivotButton";
import GridToolbarFiltersButton from "../toolbar/GridToolbarFiltersButton";
import CachedIcon from "@mui/icons-material/Cached";
import styled from "@emotion/styled";
import GridToolbarColumnsButton from "../toolbar/GridToolbarColumnsButton";
import GridToolbarDensitySelector from "../toolbar/GridToolbarDensitySelector";

export default function SaitGrid(props) {
	/////////////// DEFAULT PARAMETERS ///////////////
	// MANDATORY:
	//     rows:            Righe da visualizzare in griglia
	//     columns:         Colonne della griglia
	//     isFiltering:     Parametro che maschera la griglia quando sta filtrando
	//     pageSize:        Numero di righe per pagina
	const defaultMinHeight = 200; // Altezza minima griglia
	//////////////////////////////////////////////////

	const componentModule = useContext(ModuleContext);
	const userPreferences = useSelector((state) => state["auth"]);
	const objModArray = userPreferences["objMod"];

	const [selectionModel, setSelectionModel] = React.useState({
		type: "include",
		ids: new Set([]),
	});
	const [columnVisibilityModel, setColumnVisibilityModel] = React.useState();

	const [paginationModel, setPaginationModel] = React.useState({
		pageSize: props.pageSize,
		page: props.paginationMode !== "client" ? props.page : 0,
	});

	const [columnsGrid, setColumnsGrid] = React.useState([]);

	const [visualizeGrid, setVisualizeGrid] = React.useState(true);

	let activeTabId = useSelector((state) => state["generalConfig"]["activeTabId"]);

	const gridId = props.gridId || props.findFunction ? "GRID_" + (props.gridId || componentModule + props.findFunction) : null;

	//SETTINGS

	const settingsStorageKey = "GRID_" + (props.gridId || componentModule) + "_SETTINGS";

	const SETTINGS_DEFAULT = {
		density: "standard",
		showCellBorders: false,
		showColumnBorders: false,
	};

	const getInitialSettings = () => {
		try {
			const storedSettings = localStorage.getItem(settingsStorageKey);
			return storedSettings ? JSON.parse(storedSettings) : SETTINGS_DEFAULT;
		} catch (error) {
			return SETTINGS_DEFAULT;
		}
	};
	const [settings, setSettings] = React.useState(getInitialSettings());

	//USE-EFFECT

	useEffect(() => {
		if (componentModule === activeTabId || !activeTabId) {
			setVisualizeGrid(true);
		} else {
			setVisualizeGrid(false);
		}
	}, [activeTabId]);

	useEffect(() => {
		const savedColumns = props.gridId || props.findFunction ? getSavedColumns() : null;

		let newColumnsFind = false;

		const newColumns = props.columns.map((column) => {
			if (props.gridId || props.findFunction) {
				const matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === column.field);

				if (matchingSavedColumn) {
					column.hidden = matchingSavedColumn?.hidden;

					if (matchingSavedColumn?.width) {
						column.width = matchingSavedColumn.width;
					}
				} else {
					const columnToAdd = {
						field: column.field,
						width: column.width,
						hidden: column.hidden,
					};

					savedColumns.push(columnToAdd);

					newColumnsFind = true;
				}
			}

			return manageColumn(column);
		});

		if (newColumnsFind && (props.gridId || props.findFunction)) {
			localStorage.setItem(gridId, JSON.stringify(savedColumns));
		}

		setColumnVisibilityModel(
			props.columns.reduce((acc, item) => {
				acc[item.field] = !item.hidden;
				return acc;
			}, {})
		);

		setColumnsGrid(newColumns);
	}, [props.columns]);

	useEffect(() => {
		if (props.selectFirstRow) {
			if (props.rows?.length > 0) {
				cambiaSelezione({
					type: "include",
					ids: new Set([props.rows[0].id]),
				});
			}
		}
		// eslint-disable-next-line
	}, [props.rows, props.selectFirstRow]);

	useEffect(() => {
		if (props.page !== paginationModel.page) {
			setPaginationModel({
				pageSize: props.pageSize,
				page: props.paginationMode !== "client" ? props.page : 0,
			});
		}
	}, [props.page]);

	useEffect(() => {
		localStorage.setItem(settingsStorageKey, JSON.stringify(settings));
	}, [settings]);

	const manageColumn = (column) => {
		if (column.valueFormatter || column.actionColumn) {
			return column;
		} else {
			// Metto il tooltip ai dati che vengono troncati perchè troppo lunghi
			let renderCell = column.renderCell;
			if (!renderCell) {
				renderCell = (params) => {
					if (column.tooltip) {
						return (
							<Tooltip
								title={
									<Typography sx={{ whiteSpace: "pre-line" }} fontSize={14}>
										{formatLabel(column.tooltip, params.row)}
									</Typography>
								}
							>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{params.value}
								</span>
							</Tooltip>
						);
					} else {
						const charPixels = 7.6;
						const columnWidth = params.colDef.computedWidth;
						if (params.value) {
							const stringLength = params.value.length;
							const isStringCut = columnWidth < stringLength * charPixels;
							if (isStringCut) {
								return (
									<Tooltip title={<Typography fontSize={14}>{params.value}</Typography>}>
										<span
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{params.value}
										</span>
									</Tooltip>
								);
							} else {
								return <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{params.value}</span>;
							}
						}
					}
				};
			}
			return {
				...column,
				renderCell: renderCell,
			};
		}
	};

	const getSavedColumns = () => {
		const savedColumnsString = localStorage.getItem(gridId);
		let savedColumns;
		if (savedColumnsString && savedColumnsString !== "undefined" && !window["Configs"].configTest) {
			try {
				savedColumns = JSON.parse(savedColumnsString);
			} catch (error) {
				savedColumns = null;
			}
		}

		if (!savedColumns || savedColumns.length === 0) {
			const columnsToSave = props.columns?.map((column) => {
				const objMod = objModArray.find((objModObject) => objModObject.moduleName === componentModule && objModObject.idObject === column.objmod);

				let columnToSave = {
					field: column.field,
					width: column.width,
					hidden: !isComponentVisible(objMod, column.hidden),
				};
				return columnToSave;
			});

			localStorage.setItem(gridId, JSON.stringify(columnsToSave));
			savedColumns = columnsToSave;
		}

		return savedColumns;
	};

	const cambiaSelezione = (newSelectionModel) => {
		//Cerco gli ID di newSelectionModel nelle righe perchè se viene rieseguita la find cambiano gli ID delle righe
		let selectionArray = [...newSelectionModel.ids];
		if (props.selection?.multipleSelection) {
			let multipleElementArray = [];
			let multipleElementArrayId = [];
			if (newSelectionModel.type === "exclude") {
				//selezionate tutte le celle
				props.rows.forEach((row) => {
					multipleElementArray.push(row);
					multipleElementArrayId.push(row.id);
				});
			} else {
				selectionArray.forEach((selectedId) => {
					let rigaSelezionata = props.rows.find((row) => row.id === selectedId);
					if (rigaSelezionata) {
						multipleElementArray.push(rigaSelezionata);
						multipleElementArrayId.push(rigaSelezionata.id);
					}
				});
			}
			if (props.selection?.selectionFunction) {
				props.selection.selectionFunction(multipleElementArray);
			}
			setSelectionModel({
				type: "include",
				ids: new Set(multipleElementArrayId),
			});
		} else {
			const rigaSelezionata = props.rows.find((row) => row.id === selectionArray[newSelectionModel.ids.size - 1]);

			let singleElementArrayId = [];
			if (rigaSelezionata) {
				singleElementArrayId.push(rigaSelezionata.id);
			}

			if (props.selection?.selectionFunction) {
				props.selection.selectionFunction(rigaSelezionata);
			}

			if (props.setSelectedRecord) {
				props.setSelectedRecord(rigaSelezionata);
			}
			setSelectionModel({
				type: "include",
				ids: new Set(singleElementArrayId),
			});
		}
	};

	function onFilterChange(newFilters) {
		let newGridFilters = newFilters.items;
		let mergedFilters = [...props.gridFilters];

		newGridFilters.forEach((newGridFilter) => {
			const existingFilterIndex = mergedFilters.findIndex((filter) => filter.id === newGridFilter.id);

			if (existingFilterIndex !== -1) {
				if (newGridFilter.value || newGridFilter.value === 0 || newGridFilter.operator === "isEmpty" || newGridFilter.operator === "isNotEmpty") {
					// Aggiorno il filtro
					mergedFilters[existingFilterIndex] = newGridFilter;
				} else {
					// Rimuovi il filtro
					mergedFilters = mergedFilters.filter((filter) => filter.id !== newGridFilter.id);
				}
			} else {
				// Aggiungo il filtro
				if (newGridFilter.value || newGridFilter.value === 0 || newGridFilter.operator === "isEmpty" || newGridFilter.operator === "isNotEmpty") {
					mergedFilters.push(newGridFilter);
				}
			}
		});

		props.changeGridFilters?.(mergedFilters);
	}

	function onChangeVisibilityCustom(newModel) {
		if (props.gridId || props.findFunction) {
			const savedColumns = getSavedColumns();

			if (Object.keys(newModel).length === 0) {
				//tutte le colonne visibili
				savedColumns.map((savedColumn) => (savedColumn.hidden = false));
			} else {
				for (const column in newModel) {
					if (newModel.hasOwnProperty(column)) {
						const hidden = !newModel[column];

						const matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === column);

						if (matchingSavedColumn) {
							matchingSavedColumn.hidden = hidden;
						} else {
							const columnToAdd = {
								field: column,
								width: 120,
								hidden: hidden,
							};

							savedColumns.push(columnToAdd);
						}
					}
				}
			}

			localStorage.setItem(gridId, JSON.stringify(savedColumns));
		}

		setColumnVisibilityModel(newModel);
	}

	function onColumnResizeCustom(column) {
		if (props.gridId || props.findFunction) {
			const savedColumns = getSavedColumns();

			let matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === column.colDef.field);

			if (matchingSavedColumn) {
				matchingSavedColumn.width = column.width;
			} else {
				const columnToAdd = {
					field: column.colDef.field,
					width: column.width,
					hidden: false,
				};

				savedColumns.push(columnToAdd);
			}

			localStorage.setItem(gridId, JSON.stringify(savedColumns));
		}

		let newColumnsGrid = columnsGrid;
		newColumnsGrid.find((newColumnGrid) => newColumnGrid.field === column.colDef.field).width = column.width;
		setColumnsGrid(newColumnsGrid);
	}

	function onPageChange(newPage) {
		if (props.paginationMode === "client" || props.page !== newPage.page) {
			setPaginationModel({
				pageSize: props.pageSize,
				page: newPage.page,
			});
			if (props.paginationMode !== "client") {
				props.handlePageChanged(newPage.page);
			}
		}
	}

	const StyledToolbar = styled(Toolbar)`
		display: flex;
		justify-content: space-between;
		align-items: center;
		min-height: 37px;
	`;

	function CustomToolbar() {
		return (
			<StyledToolbar>
				{props.noStandardToolbar ? (
					<Box />
				) : (
					<Box>
						<SaitButton loading={props.isFiltering} onClick={props.handleFireFilter} icon={<CachedIcon />} variant="text" disabled={props.disableToolbarFilterButton} />
						<GridToolbarColumnsButton />
						<GridToolbarDensitySelector settings={settings} onSettingsChange={setSettings} />
						<GridToolbarFiltersButton
							gridFilters={props.gridFilters}
							changeGridFilters={props.changeGridFilters}
							columnsGrid={columnsGrid}
							isFiltering={props.isFiltering}
							savedFilters={props.savedFilters}
							applySavedFilters={props.applySavedFilters}
							deleteSavedFilter={props.deleteSavedFilter}
							setNewReportName={props.setNewReportName}
						/>
						<GridToolbarExcelButton
							rowCount={props.rowCount}
							columns={props.columns}
							columnsVisibility={columnVisibilityModel}
							fetchDataForExcel={props.fetchDataForExcel}
							rowBackgroundColor={props.rowBackgroundColor}
							isFiltering={props.isFiltering}
							rows={props.rows}
						/>
						{props.pivotFunction && (
							<GridToolbarExcelPivotButton
								pivotFunction={props.pivotFunction}
								rowCount={props.rowCount}
								columns={props.columns}
								columnsVisibility={columnVisibilityModel}
								fetchDataForExcel={props.fetchDataForExcel}
								isFiltering={props.isFiltering}
								rows={props.rows}
							/>
						)}
					</Box>
				)}
				<Box>
					{props.topButtons?.map((topButton, index) => {
						switch (topButton.type) {
							case BUTTON_UPLOAD:
								return (
									<SaitButtonUpload
										key={index}
										sx={{ ml: "5px" }}
										text={topButton.text}
										onUpload={topButton.onUpload}
										endIcon={topButton.icon}
										multiple={topButton.multiple}
										accept={topButton.accept}
										variant={topButton.variant || "text"}
										disabled={topButton.disabled || !checkIsRoleAllowed(topButton.roleSecurity, userPreferences)}
										objmod={topButton.objmod}
									/>
								);
							case BUTTON_LABEL:
								return (
									<Button
										key={index}
										variant="text"
										disableElevation
										disableFocusRipple
										disableRipple
										tabIndex={-1}
										sx={{
											textTransform: "none",
											color: "#000",
											pointerEvents: "none",
											"&:focus": {
												outline: "none",
											},
											"&.Mui-focusVisible": {
												boxShadow: "none",
												outline: "none",
											},
										}}
									>
										{topButton.text}
									</Button>
								);
							default:
								return (
									<SaitButton
										key={index}
										sx={{ ml: "5px" }}
										text={topButton.text}
										loading={props.isFiltering}
										onClick={topButton.function}
										endIcon={topButton.icon}
										color={topButton.color || "primary"}
										variant={topButton.variant || "text"}
										disabled={topButton.disabled || !checkIsRoleAllowed(topButton.roleSecurity, userPreferences)}
										objmod={topButton.objmod}
										hidden={topButton.hidden ? topButton.hidden : false}
									/>
								);
						}
					})}
				</Box>
			</StyledToolbar>
		);
	}

	const CustomFilterMenu = (props) => {
		const { colDef } = props;

		return <div style={{ zIndex: 99999 }}>{colDef.headerName}</div>;
	};

	const CustomFilterPanel = (props) => {
		const apiContext = useGridApiContext();

		const handleKeyDown = (event) => {
			if (event.key === "Enter") {
				// Chiudi il pannello dei filtri
				apiContext.current.hideFilterPanel();
			}
		};

		return (
			// @ts-ignore
			<div onKeyDown={handleKeyDown}>
				<GridFilterPanel {...props} />
			</div>
		);
	};

	return (
		visualizeGrid && (
			<Box
				sx={{
					minHeight: props.height ? props.height : props.minHeight || defaultMinHeight,
					flex: 1,
					position: "relative",
					...(props.disabled && {
						pointerEvents: "none",
						opacity: 0.6,
					}),
				}}
			>
				<Box sx={{ position: "absolute", inset: 0 }}>
					<DataGrid
						slots={{
							toolbar: CustomToolbar,
							filterMenu: CustomFilterMenu,
							filterPanel: CustomFilterPanel,
							...props.slots,
						}}
						hideFooterPagination={props.isFiltering}
						sx={{
							boxShadow: 1,
							border: 1,
							borderColor: "primary.light",
							"& .MuiDataGrid-columnHeader": {
								backgroundColor: "primary.light",
							},
							"& .MuiDataGrid-row:nth-of-type(even)": {
								backgroundColor: "evenRowGrid.main",
							},
							"& .MuiDataGrid-row:nth-of-type(odd)": {
								backgroundColor: "background.default",
							},
							"& .MuiDataGrid-row.Mui-selected": {
								color: "primary.dark",
								backgroundColor: "primary.light",
							},
							"& .MuiDataGrid-row:hover": {
								backgroundColor: "hoverRowGrid.main",
							},
							"& .MuiDataGrid-footerContainer": {
								height: "30px",
								minHeight: "30px",
							},
							"& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
								outline: "none",
							},
							"& .MuiTablePagination-root": {
								maxHeight: "unset !important",
							},
							minHeight: props.height ? props.height : props.minHeight || defaultMinHeight,
						}}
						slotProps={{
							panel: {
								// @ts-ignore
								placement: "bottom-start", //Posizione del pannello colonne
							},
						}}
						getRowClassName={(params) => {
							if (props.rowBackgroundColor) {
								return props.rowBackgroundColor(params.row) || "";
							}
						}}
						rows={props.rows}
						columns={columnsGrid.map((col) => ({
							...col,
							type: col.type || "string",
						}))}
						paginationModel={paginationModel}
						onPaginationModelChange={onPageChange}
						pageSizeOptions={[props.pageSize]}
						checkboxSelection={props.selection?.checkboxSelection || props.selection?.multipleSelection}
						loading={props.isFiltering}
						paginationMode={props.paginationMode || "server"}
						rowCount={props.paginationMode !== "client" ? props.rowCount : undefined}
						sortingMode={props.sortingMode || "server"}
						onSortModelChange={props.sortingMode !== "client" ? props.handleSortChanged : undefined}
						rowHeight={props.rowHeight || 26}
						columnHeaderHeight={props.headerHeight || 32}
						hideFooterSelectedRowCount={!props.selection?.multipleSelection}
						onRowSelectionModelChange={(newSelectionModel) => {
							cambiaSelezione(newSelectionModel);
						}}
						// @ts-ignore rowSelectionModel da errore sui tipi ma funziona
						rowSelectionModel={selectionModel}
						localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
						onRowDoubleClick={props.handleRowDoubleClick}
						hideFooter={props.hideFooter}
						isRowSelectable={props.isRowSelectable}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={(newModel) => onChangeVisibilityCustom(newModel)}
						onColumnResize={(column) => onColumnResizeCustom(column)}
						filterMode="server"
						onFilterModelChange={onFilterChange}
						filterDebounceMs={300}
						onCellClick={props.onCellClick}
						processRowUpdate={props.afterEdit}
						showToolbar={!props.noStandardToolbar || props.topButtons?.length > 0}
						density={settings.density}
						showCellVerticalBorder={settings.showCellBorders}
						showColumnVerticalBorder={settings.showColumnBorders}
					/>
				</Box>
			</Box>
		)
	);
}
